// ─────────────────────────────────────────────────────────────────────────────
// graphLabels — a small, prioritised, non-overlapping set of DOM labels.
//
// NEVER A LABEL PER FILE. A hundred thousand DOM nodes would cost more than the
// entire WebGL scene. There is a hard budget, and what gets to spend it is
// decided by priority: what you selected, what you are pointing at, then the
// structure, then the search hit.
//
// DEVIATION, DECLARED: the specification names CSS2DRenderer. This projects the
// positions directly and hands React a plain list instead, because the standing
// freeze is "OrbitControls the only addon" and a projection is about thirty
// lines. The behaviour the spec asks for — a small number of important labels,
// budgeted and overlap-suppressed — is unchanged. Say the word and it swaps to
// CSS2DRenderer.
//
// SELECTION-FIRST, AND THE DEFAULT IS ZERO. The rejected G1 policy labelled City
// Hall, every domain and up to a hundred and twenty agents, so forty labels were
// on screen before anything was clicked and the graph read as a diagram. Text
// now appears because something was SELECTED, never because the graph loaded.
// ─────────────────────────────────────────────────────────────────────────────

import * as THREE from 'three';
import { Vec3 } from './graphLayout';
import { RexyGraphNodeKind } from './graphTypes';

/** DOM labels visible at once. A LABEL budget — never a graph-node cap. */
export const LABEL_BUDGET = 40;

/** Contextual caps, owner-ruled. They do NOT flex upward with child count. */
export const DOMAIN_CONTEXT_LABEL_CAP = 16;
export const AGENT_CONTEXT_LABEL_CAP = 12;
/** Temporary labels while the search field is focused. */
export const SEARCH_LABEL_CAP = 20;

export type GraphLabelMode = 'selection' | 'context' | 'all';

export interface LabelCandidate {
  id: string;
  label: string;
  position: Vec3;
  kind: RexyGraphNodeKind;
  colour: string;
  /** Lower sorts first. See PRIORITY below. */
  priority: number;
}

export interface PlacedLabel {
  id: string;
  label: string;
  x: number;
  y: number;
  colour: string;
  emphasis: boolean;
}

/** The specification's ladder, as numbers so the sort is one comparison. */
export const PRIORITY = {
  selected: 0,
  hovered: 1,
  cityHall: 2,
  domain: 3,
  agentInFocus: 4,
  systemInFocus: 5,
  searchHit: 6,
  nearby: 7,
};

const MIN_X_GAP = 96;
const MIN_Y_GAP = 16;

export interface LabelPolicyInput {
  selectedId: string | null;
  hoveredId: string | null;
  labelMode: GraphLabelMode;
  searchResultIds: string[];
  /** True only while the search field holds focus. */
  searchActive: boolean;
}

/**
 * WHO IS ALLOWED A LABEL, and nothing about where it lands on screen.
 *
 * Returns node ids with their priority. The caller resolves positions, colours
 * and projection — keeping the policy free of the camera means it can be
 * reasoned about (and asserted) without a renderer.
 *
 * Nothing selected, in the default mode, returns an EMPTY LIST. That is the
 * headline behaviour of this pass, so it is the first branch in the function.
 */
export function labelCandidateIds(
  input: LabelPolicyInput,
  kindOf: (id: string) => string | undefined,
  childrenOf: (id: string) => string[],
  parentOf: (id: string) => string | undefined,
): Array<{ id: string; priority: number }> {
  const out: Array<{ id: string; priority: number }> = [];
  const seen: { [id: string]: true } = {};
  const push = (id: string | null | undefined, priority: number) => {
    if (!id || seen[id]) return;
    seen[id] = true;
    out.push({ id, priority });
  };

  // Search results are the one thing that may label without a selection, and
  // only while the field is focused. They vanish with the query.
  if (input.searchActive) {
    for (let i = 0; i < input.searchResultIds.length && i < SEARCH_LABEL_CAP; i++) {
      push(input.searchResultIds[i], PRIORITY.searchHit);
    }
  }

  const selected = input.selectedId;
  if (!selected) {
    // 'all' remains an explicit diagnostic choice and still obeys the global
    // budget; it is never reached by loading the page.
    return out;
  }

  push(selected, PRIORITY.selected);
  const kind = kindOf(selected);

  if (kind === 'domain') {
    const agents = childrenOf(selected);
    let used = 1;
    for (let i = 0; i < agents.length && used < DOMAIN_CONTEXT_LABEL_CAP; i++) {
      if (kindOf(agents[i]) !== 'agent') continue;
      push(agents[i], PRIORITY.agentInFocus);
      used++;
    }
    // The hovered node earns a label only if it is a direct child — hover never
    // labels the graph at large.
    if (input.hoveredId && parentOf(input.hoveredId) === selected) {
      push(input.hoveredId, PRIORITY.hovered);
    }
    return out;
  }

  if (kind === 'agent') {
    push(parentOf(selected), PRIORITY.cityHall);
    const systems = childrenOf(selected);
    let used = 2;
    for (let i = 0; i < systems.length && used < AGENT_CONTEXT_LABEL_CAP; i++) {
      push(systems[i], PRIORITY.systemInFocus);
      used++;
    }
    if (input.hoveredId && parentOf(input.hoveredId) === selected) {
      push(input.hoveredId, PRIORITY.hovered);
    }
    return out;
  }

  // A leaf labels ITSELF and nothing else. Its path belongs in the inspector.
  return out;
}

/**
 * Project, sort, budget, suppress. Returns screen-space labels in the order they
 * should be rendered.
 *
 * Overlap suppression is a straight rejection test against everything already
 * placed: because the list is priority-sorted, the label that loses a collision
 * is always the less important one.
 */
export function placeLabels(
  candidates: LabelCandidate[],
  camera: THREE.PerspectiveCamera,
  width: number,
  height: number,
  selectedId: string | null,
): PlacedLabel[] {
  const projected: Array<PlacedLabel & { priority: number; depth: number }> = [];
  const v = new THREE.Vector3();

  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    v.set(c.position[0], c.position[1], c.position[2]);
    v.project(camera);

    // Behind the camera, or outside the frame with a margin — a label pinned to
    // the edge for something off screen points at nothing.
    if (v.z > 1 || v.x < -1.05 || v.x > 1.05 || v.y < -1.05 || v.y > 1.05) continue;

    projected.push({
      id: c.id,
      label: c.label,
      x: ((v.x + 1) / 2) * width,
      y: ((1 - v.y) / 2) * height,
      colour: c.colour,
      emphasis: c.id === selectedId,
      priority: c.priority,
      depth: v.z,
    });
  }

  projected.sort((a, b) => (a.priority - b.priority) || (a.depth - b.depth));

  const placed: PlacedLabel[] = [];
  for (let i = 0; i < projected.length && placed.length < LABEL_BUDGET; i++) {
    const p = projected[i];
    let collides = false;
    for (let j = 0; j < placed.length; j++) {
      if (Math.abs(placed[j].x - p.x) < MIN_X_GAP && Math.abs(placed[j].y - p.y) < MIN_Y_GAP) {
        collides = true;
        break;
      }
    }
    if (collides) continue;
    placed.push({ id: p.id, label: p.label, x: p.x, y: p.y, colour: p.colour, emphasis: p.emphasis });
  }

  return placed;
}
